<script lang="ts">
	import { AdvancedCardV2, LabelListV2, LabelV2 } from '@Shelly/shelly-design-system';
	import { onDestroy } from 'svelte';

	import {
		resolveFlowCardSurfaceGlow,
		type FlowCardSemanticVariant,
		type FlowNodeAvatarConfig,
	} from '../helpers/flowNodeCardPresentation';
	import type { FlowVisualRole } from '../helpers/flowCardVisualLanguage';

	/** Tracks card height so parent can pin handles on the header row. */
	export let dividerOffsetPx = 44;

	export let canvasTheme: 'dark' | 'light' = 'dark';

	export let semanticVariant: FlowCardSemanticVariant;
	export let visualRole: FlowVisualRole;
	export let roleLabel: string;
	export let roleIcon: string;
	export let isComposite = false;

	/** Primary title — usually device name or node label. */
	export let title: string;
	/** Plain-language description of the configured behavior. */
	export let sentence: string | undefined = undefined;

	/** Muted category line — omit when the card body carries the main info. */
	export let description: string | undefined;

	/** Short summary when there is no custom body slot (AdvancedCard hides main when set). */
	export let subtitle: string | undefined;

	/** Human-readable node category displayed below the primary title. */
	export let nodeType: string | undefined;

	export let subtitleType:
		| 'default'
		| 'primary'
		| 'secondary'
		| 'success'
		| 'warning'
		| 'danger'
		| 'disabled' = 'default';

	export let avatar: FlowNodeAvatarConfig | undefined;

	/** Optional pill on the right (e.g. execution mode). */
	export let badge: string | undefined;

	export let labelList: { icon?: string; value?: string; iconColor?: string }[] | undefined;

	export let selected = false;

	/** Whether the caller actually supplied card body content. */
	export let hasBodyContent = false;

	let rootEl: HTMLElement | undefined;
	let ro: ResizeObserver | undefined;

	function attachObserver(el: HTMLElement | undefined) {
		if (ro) {
			ro.disconnect();
			ro = undefined;
		}
		if (typeof ResizeObserver === 'undefined' || !el) return;

		ro = new ResizeObserver(() => {
			const card = el.querySelector('.flow-node-advanced-card') as HTMLElement | null;
			const height = card?.offsetHeight ?? el.offsetHeight;
			dividerOffsetPx = Math.max(
				44,
				Math.round(height * (hasBodyContent || labelList?.length ? 0.38 : 0.5)),
			);
		});
		ro.observe(el);
		const card = el.querySelector('.flow-node-advanced-card') as HTMLElement | null;
		dividerOffsetPx = Math.max(
			44,
			Math.round(
				(card?.offsetHeight ?? el.offsetHeight) *
					(hasBodyContent || labelList?.length ? 0.38 : 0.5),
			),
		);
	}

	$: attachObserver(rootEl);

	onDestroy(() => {
		if (ro) ro.disconnect();
	});

	$: themeClass = canvasTheme === 'light' ? 'flow-nc-theme-light' : 'flow-nc-theme-dark';
	$: variantClass = `flow-nc-var-${semanticVariant}`;
	$: useCondensedLayout = !hasBodyContent && !labelList?.length;
	$: hideMainSlot = !labelList?.length && !hasBodyContent;
	$: visibleSubtitle = (nodeType || subtitle || '').trim();
	$: advancedSubtitle = visibleSubtitle
		? {
				value: visibleSubtitle,
				semiBold: false,
				type: nodeType ? ('secondary' as const) : subtitleType,
				textSmall: true,
			}
		: undefined;
	$: advancedDescription = description
		? {
				value: description,
				type: 'muted' as const,
				textSmall: true,
				semiBold: false,
			}
		: undefined;
	$: surfaceGlow = resolveFlowCardSurfaceGlow(semanticVariant);
</script>

<div
	class="flow-node-card-root {themeClass} {variantClass}"
	class:flow-node-card-root--rich={hasBodyContent || Boolean(labelList?.length)}
	class:flow-node-card-root--composite={isComposite}
	data-flow-role={visualRole}
	bind:this={rootEl}
>
	{#if isComposite}
		<div class="composite-mark" title="Composite template" aria-label="Composite template">
			<i class="fa-solid fa-layer-group" aria-hidden="true"></i>
		</div>
	{/if}
	<AdvancedCardV2
		class="flow-node-advanced-card"
		aria-label={title}
		{title}
		subtitle={advancedSubtitle}
		description={advancedDescription}
		{avatar}
		active={true}
		{surfaceGlow}
		{selected}
		isCondensed={useCondensedLayout}
		isFocusable={false}
		hideSlotRight
		hideSlotMain={hideMainSlot}
		hideSlotRightBadge={!badge}
	>
		<div slot="main" class="flow-node-card__body">
			{#if sentence}
				<div class="flow-node-card__sentence">{sentence}</div>
			{/if}
			{#if labelList?.length}
				<LabelListV2 size="s" {labelList} a11yProps={{ isInteractive: false }} />
			{:else}
				<slot />
			{/if}
		</div>

		<LabelV2 slot="rightBadge" size="s" value={badge || ''} type="primary" />
	</AdvancedCardV2>
</div>

<style>
	.flow-node-card-root {
		position: relative;
		min-width: 17rem;
		width: 20rem;
		max-width: min(20rem, calc(100vw - 2rem));
	}

	.flow-node-card-root--composite::before {
		content: '';
		position: absolute;
		inset: 5px -5px -5px 5px;
		z-index: -1;
		border: 1px solid color-mix(in srgb, var(--flow-node-card-accent) 55%, transparent);
		border-radius: 0.85rem;
		background: color-mix(in srgb, var(--flow-node-card-accent) 7%, transparent);
	}

	.composite-mark {
		position: absolute;
		top: -0.48rem;
		right: 0.8rem;
		z-index: 4;
		display: grid;
		width: 1.35rem;
		height: 1.35rem;
		place-items: center;
		border: 1px solid color-mix(in srgb, var(--flow-node-card-accent) 72%, transparent);
		border-radius: 0.4rem;
		background: color-mix(in srgb, var(--flow-node-card-accent) 20%, #202124 80%);
		color: #f8fafc;
		font-size: 0.62rem;
	}

	.flow-node-card-root--rich {
		/* Keep the measured node box identical to the visible card surface. */
		min-height: 0;
	}

	.flow-node-card-root--rich :global(.flow-node-advanced-card) {
		min-height: 5.25rem;
	}

	.flow-node-card-root--rich :global(.flow-node-advanced-card .main) {
		padding-top: 0.25rem;
		padding-bottom: 0.35rem;
	}

	.flow-node-card-root :global(.flow-node-advanced-card) {
		width: 100%;
		box-sizing: border-box;
		border: 1px solid
			color-mix(in srgb, var(--flow-node-card-accent, var(--color-lightblue)) 80%, transparent);
		pointer-events: none;
		user-select: none;
		overflow: hidden;
	}

	.flow-node-card-root :global(.flow-node-advanced-card .main .content) {
		min-width: 0;
	}

	.flow-node-card-root :global(.flow-node-advanced-card .main .content > div:first-child) {
		min-width: 0;
	}

	.flow-node-card-root
		:global(.flow-node-advanced-card .main .content > div:first-child .text-nowrap) {
		display: -webkit-box;
		overflow: hidden;
		white-space: normal;
		text-overflow: ellipsis;
		-webkit-box-orient: vertical;
		-webkit-line-clamp: 2;
		line-clamp: 2;
	}

	/* AdvancedCardV2 has one hotspot; mirror it to light both upper corners. */
	.flow-node-card-root :global(.flow-node-advanced-card .widget-lighting-spot) {
		inset: 0 !important;
		width: 100% !important;
		height: 100% !important;
		transform: none !important;
		border-radius: inherit;
		background:
			radial-gradient(
				circle var(--lighting-interior-size) at 0 0,
				rgb(var(--lighting-accent-rgb) / calc(var(--lighting-interior-strength) * 0.55)),
				transparent 72%
			),
			radial-gradient(
				circle var(--lighting-interior-size) at 100% 0,
				rgb(var(--lighting-accent-rgb) / calc(var(--lighting-interior-strength) * 0.55)),
				transparent 72%
			) !important;
	}

	.flow-node-card-root :global(.flow-node-advanced-card .widget-lighting-edge) {
		background:
			radial-gradient(
				var(--lighting-edge-size) var(--lighting-edge-size) at 0 0,
				rgb(var(--lighting-accent-rgb) / calc(var(--lighting-edge-strength) * 0.82)),
				transparent 68%
			),
			radial-gradient(
				var(--lighting-edge-size) var(--lighting-edge-size) at 100% 0,
				rgb(var(--lighting-accent-rgb) / calc(var(--lighting-edge-strength) * 0.82)),
				transparent 68%
			) !important;
	}

	.flow-node-card-root :global(.flow-node-advanced-card.advanced-card-v2.selected) {
		box-shadow: 0 0 0 2px rgba(47, 125, 246, 0.55);
	}

	.flow-nc-var-trigger {
		--flow-node-card-accent: var(--color-cyan);
	}

	.flow-nc-var-condition {
		--flow-node-card-accent: var(--color-lightblue);
	}

	.flow-nc-var-action {
		--flow-node-card-accent: var(--color-green);
	}

	.flow-nc-var-warning {
		--flow-node-card-accent: var(--color-orange);
	}

	.flow-nc-theme-dark :global(.flow-node-advanced-card) {
		background-color: #242527 !important;
		--flow-slot-strong: #f1f5f9;
		--flow-slot-muted: #94a3b8;
		--flow-slot-soft: #cbd5e1;
		--flow-chip-fg: #e2e8f0;
		--flow-chip-border: rgba(148, 163, 184, 0.35);
		--flow-swatch-border: rgba(226, 232, 240, 0.4);
		--color-text-primary: #f1f5f9;
		--color-text-secondary: #cbd5e1;
		--color-text-3: #94a3b8;
	}

	.flow-nc-theme-dark :global(.flow-node-advanced-card .main) {
		color: #f8fafc;
	}

	.flow-nc-theme-dark :global(.flow-node-advanced-card .main .text-secondary),
	.flow-nc-theme-dark :global(.flow-node-advanced-card .main .text-color-muted) {
		color: #cbd5e1 !important;
	}

	.flow-nc-theme-light :global(.flow-node-advanced-card) {
		background-color: #ffffff !important;
		--flow-slot-strong: #0f172a;
		--flow-slot-muted: #475569;
		--flow-slot-soft: #334155;
		--flow-chip-fg: #334155;
		--flow-chip-border: rgba(51, 65, 85, 0.3);
		--flow-swatch-border: rgba(15, 23, 42, 0.3);
		--color-text-primary: #0f172a;
		--color-text-secondary: #475569;
		--color-text-3: #64748b;
	}

	.flow-nc-theme-light .composite-mark {
		background: color-mix(in srgb, var(--flow-node-card-accent) 13%, #fff 87%);
		color: #0f172a;
	}

	.flow-nc-theme-light :global(.flow-node-advanced-card .main) {
		color: #0f172a;
	}

	.flow-nc-theme-light :global(.flow-node-advanced-card .main .text-secondary),
	.flow-nc-theme-light :global(.flow-node-advanced-card .main .text-color-muted) {
		color: #475569 !important;
	}

	/* Design-system avatars default to near-white icons. Keep them legible on light cards. */
	.flow-nc-theme-light :global(.flow-node-advanced-card .circle-avatar.no-background .icon-grid),
	.flow-nc-theme-light :global(.flow-node-advanced-card .circle-avatar.no-background .icon-grid i),
	.flow-nc-theme-light :global(.flow-node-advanced-card .circle-avatar.no-background .icon-grid svg) {
		color: #0f172a !important;
		fill: currentColor;
	}

	.flow-node-card__body {
		font-size: 0.8125rem;
		line-height: 1.5;
		overflow-wrap: anywhere;
		white-space: normal;
		word-break: break-word;
		color: var(--flow-slot-soft, #334155);
	}

	.flow-node-card__sentence {
		margin-bottom: 0.35rem;
		color: var(--flow-slot-strong, #0f172a);
		font-size: 0.82rem;
		font-weight: 600;
		line-height: 1.35;
	}

	.flow-node-card__sentence:last-child {
		margin-bottom: 0;
	}

	.flow-node-card__body :global(*) {
		white-space: normal;
	}
</style>
